import api from "@/src/api/api";
import { config } from "@/src/config/config";
import type {
  AiVulnerabilityAnalysis,
  ThreatTrendsResponse,
} from "@/src/pages/ThreatIntel/types";

const BASE_URL_DASHBOARD = config.API_URL;

export interface DashboardCombinedResponse {
  data?: {
    codeguard?: any;
    vscan?: any;
    leakguard?: any;
    threat_intel?: any;
    dspm?: any;
    cspm?: any;
  };
  errors?: unknown;
  modules?: string[];
  elapsed_ms?: number;
}

export type DashboardOverviewModule =
  | "codeguard_overview"
  | "vscan_overview"
  | "leakguard_overview";

export interface DashboardDataResponse<T = any> {
  data?: Partial<Record<DashboardOverviewModule, T>>;
  errors?: unknown;
  modules?: DashboardOverviewModule[];
  elapsed_ms?: number;
}

export const DashboardService = {
  getCombinedDashboard: async (): Promise<DashboardCombinedResponse> => {
    try {
      const response = await api.get(`${BASE_URL_DASHBOARD}/api/dashboard/combined`, {
        headers: { Accept: "application/json" },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching combined dashboard data:", error);
      throw error;
    }
  },

  getDashboardData: async <T = any>(
    modules: DashboardOverviewModule | DashboardOverviewModule[]
  ): Promise<DashboardDataResponse<T>> => {
    try {
      const moduleParam = Array.isArray(modules) ? modules.join(",") : modules;
      const response = await api.get(`${BASE_URL_DASHBOARD}/api/dashboard/data`, {
        params: { modules: moduleParam },
        headers: { Accept: "application/json" },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      throw error;
    }
  },

  analyseThreatAlert: async (
    alertId: string,
    forceRefresh = false
  ): Promise<AiVulnerabilityAnalysis> => {
    try {
      const response = await api.post(
        `${BASE_URL_DASHBOARD}/api/home/alerts/${alertId}/analyse`,
        null,
        {
          params: { force_refresh: forceRefresh },
          headers: { Accept: "application/json" },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching threat alert analysis:", error);
      throw error;
    }
  },

  refreshThreatTrends: async (
    windowDays = 7
  ): Promise<ThreatTrendsResponse> => {
    try {
      const response = await api.post(
        `${BASE_URL_DASHBOARD}/api/home/trends/refresh`,
        null,
        {
          params: { window_days: windowDays },
          headers: { Accept: "application/json" },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error refreshing threat trends:", error);
      throw error;
    }
  },
};
